import React from 'react';
import { Textarea } from '@/components/ui/textarea';
import { SlashTriggerContext } from '@/types/slash-commands';
import { cn } from '@/lib/utils';

interface ConclusionTextareaProps {
  value: string;
  onChange: (value: string) => void;
  isSlashMenuOpen: boolean;
  handleSlashTrigger: (context: SlashTriggerContext) => void;
  handleSlashFilterUpdate: (filterText: string) => void;
  handleSlashClose: () => void;
  handleTextareaKeyDown: (
    event: React.KeyboardEvent<HTMLTextAreaElement>
  ) => void;
  className?: string;
}

export function ConclusionTextarea({
  value,
  onChange,
  isSlashMenuOpen,
  handleSlashTrigger,
  handleSlashFilterUpdate,
  handleSlashClose,
  handleTextareaKeyDown,
  className,
}: ConclusionTextareaProps) {
  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const textarea = e.target;
    const newValue = textarea.value;
    const cursorPosition = textarea.selectionStart;
    const prevValue = value;

    onChange(newValue);

    const typedChar = newValue.charAt(cursorPosition - 1);
    const textBeforeCursor = newValue.substring(0, cursorPosition);
    const slashIndex = textBeforeCursor.lastIndexOf('/');

    if (!isSlashMenuOpen && typedChar === '/') {
      const textBeforeSlash = newValue.substring(0, cursorPosition - 1);

      // Extract word before slash
      const lastSpaceIndex = textBeforeSlash.trimEnd().lastIndexOf(' ');
      const wordBefore =
        lastSpaceIndex === -1
          ? textBeforeSlash.trim()
          : textBeforeSlash.trimEnd().substring(lastSpaceIndex + 1);

      // Extract first sentence
      const firstSentenceMatch = newValue.match(/^.*?[.!?](\s+|$)/);
      const firstSentence = firstSentenceMatch
        ? firstSentenceMatch[0].trim()
        : newValue.trim();

      const context: SlashTriggerContext = {
        targetElement: textarea,
        currentParagraphIndex: -2, // Index for conclusion
        currentParagraphType: 'conclusion',
        textBeforeTrigger: textBeforeSlash, // Full text before slash
        wordBeforeTrigger: wordBefore || null,
        firstSentenceOfParagraph: firstSentence || null,
      };
      handleSlashTrigger(context);
    } else if (isSlashMenuOpen) {
      if (
        slashIndex === -1 ||
        typedChar === ' ' ||
        newValue.length < prevValue.length // Handle deletion of slash
      ) {
        handleSlashClose();
      } else {
        const filter = newValue.substring(slashIndex + 1, cursorPosition);
        handleSlashFilterUpdate(filter);
      }
    }
  };

  return (
    <Textarea
      id='conclusion-textarea'
      placeholder='Write your conclusion...'
      value={value}
      onChange={handleTextChange}
      onKeyDown={handleTextareaKeyDown}
      className={cn('min-h-[80px] resize-none', className)}
    />
  );
}
